/**
 * Calendly Embed Vendor Catalog
 *
 * Single source of truth mapping every vendor used in booking embeds to:
 *   - A consent category (necessary | functional | performance | targeting)
 *   - The OneTrust category ID (C0001–C0004)
 *   - The primary cookies set or read by that vendor
 *   - The domains those cookies live on
 *
 * OneTrust category IDs used in this codebase:
 *   C0001  Strictly Necessary
 *   C0002  Performance / Analytics
 *   C0003  Functional
 *   C0004  Targeting / Marketing
 *
 * Consent gate in the app:  isCookiesAllowedSelector(category) in selectors/gdpr.js
 * Embed consent resolution: frontend/services/embed_consent/consent_module.js
 */

/**
 * @typedef {'necessary' | 'functional' | 'performance' | 'targeting'} ConsentCategory
 */

/**
 * @typedef {
 *   | 'calendly_core'
 *   | 'calendly_feature_flags'
 *   | 'calendly_anonymous_id'
 *   | 'calendly_booking_storage'
 *   | 'segment'
 *   | 'segment_targeting'
 *   | 'google_analytics'
 *   | 'meta_pixel'
 *   | 'datadog_rum'
 *   | 'stripe'
 *   | 'paypal'
 *   | 'recaptcha'
 *   | 'intercom'
 * } VendorKey
 */

/**
 * @typedef {Object} VendorDefinition
 * @property {VendorKey}        key                 - Stable machine-readable identifier.
 * @property {string}           displayName         - Human-readable vendor name.
 * @property {string}           description         - What this vendor does in the embed context.
 * @property {ConsentCategory}  category            - Consent category.
 * @property {string}           oneTrustCategoryId  - C0001–C0004 matching OneTrust configuration.
 * @property {string[]}         primaryCookieNames  - Cookie names set/read directly by this vendor.
 * @property {string[]}         domains             - Domains where these cookies appear.
 * @property {string}           [docsUrl]           - Internal or partner-facing docs URL.
 */

/** @type {VendorDefinition[]} */
export const VENDORS = [
  {
    key: 'calendly_core',
    displayName: 'Calendly Core',
    description: 'Session authentication, CSRF protection, and bot-detection cookies required'
      + ' for the booking flow to function. Always active; cannot be disabled.',
    category: 'necessary',
    oneTrustCategoryId: 'C0001',
    primaryCookieNames: [
      '_calendly_session', // Main auth/session token
      '_staging_calendly_session', // Staging env session token
      'OptanonConsent', // OneTrust consent record
      'OptanonAlertBoxClosed', // OneTrust banner dismiss flag
      'cookietest', // Browser cookie-support probe (assets.calendly.com)
      'cf_clearance', // Cloudflare bot-challenge clearance
      '__cf_bm', // Cloudflare Bot Management
    ],
    domains: ['calendly.com', 'assets.calendly.com', 'help.calendly.com'],
    docsUrl: 'https://calendly.com/pages/cookie-policy',
  },

  {
    key: 'calendly_feature_flags',
    displayName: 'Calendly Feature Flags (Statsig)',
    description: 'Statsig SDK used for A/B experiments and feature gates. Operates primarily'
      + ' via in-memory state and localStorage; does not set its own persistent cookies in the embed.',
    category: 'functional',
    oneTrustCategoryId: 'C0003',
    primaryCookieNames: [], // Statsig uses localStorage, not cookies, in this integration
    domains: ['calendly.com', 'featureassets.org', 'statsigapi.net'],
    docsUrl: 'https://docs.statsig.com/client/reactNativeSDK',
  },

  {
    key: 'calendly_anonymous_id',
    displayName: 'Calendly Anonymous ID',
    description: 'First-party cookie that assigns a stable anonymous identifier to a visitor'
      + ' for analytics attribution across sessions. Created and managed by cal_anonymous_id.js;'
      + ' gated on the "performance" consent group.',
    category: 'performance',
    oneTrustCategoryId: 'C0002',
    primaryCookieNames: ['cal_anonymous_id'],
    domains: ['calendly.com'],
    docsUrl: 'https://github.com/calendly/booking/blob/main/frontend/components/cal_anonymous_id.js',
  },

  {
    key: 'calendly_booking_storage',
    displayName: 'Calendly Booking Storage',
    description: 'First-party cookies written on the confirmation page to share booking data'
      + ' (invitee name, email, count) with other Calendly applications on the same domain.'
      + ' Gated on the "targeting" consent group and the "growth_booking_data_storage" feature flag.',
    category: 'targeting',
    oneTrustCategoryId: 'C0004',
    primaryCookieNames: [
      'calendly_invitee_first_name',
      'calendly_invitee_last_name',
      'calendly_invitee_email',
      'calendly_invitee_booked_publisher_name',
      'calendly_invitee_booked_count',
      'calendly_invitee_uuid_*', // CookieCounter; keyed by invitee UUID
    ],
    domains: ['calendly.com'],
    docsUrl: 'https://github.com/calendly/booking/blob/main/frontend/components/confirmation/booking-storage.js',
  },

  {
    key: 'segment',
    displayName: 'Segment Analytics',
    description: 'Segment Analytics.js is loaded from the Segment CDN and used to track booking'
      + ' events (page views, booking completed, etc.). Gated on the "performance" consent group.'
      + ' In embeds, events are also forwarded to the parent via postMessage.',
    category: 'performance',
    oneTrustCategoryId: 'C0002',
    primaryCookieNames: [
      'ajs_anonymous_id', // Segment visitor ID; 1-year persistent
      '__tld__', // Segment TLD-detection helper; session only
    ],
    domains: ['calendly.com', 'cdn.segment.io', 'cdn.segment.com', 'api.segment.io'],
    docsUrl: 'https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/',
  },

  {
    key: 'segment_targeting',
    displayName: 'Segment (Identified User Cookies)',
    description: 'Cookies written by Segment Analytics.js when analytics.identify() or'
      + ' analytics.group() is called for a known user. OneTrust classifies these as Targeting'
      + ' (C0004) because they carry user-identifiable traits. Only written after identify/group'
      + ' calls; anonymous page-view tracking is covered by the "segment" (C0002) entry.',
    category: 'targeting',
    oneTrustCategoryId: 'C0004',
    primaryCookieNames: [
      'ajs_user_id', // Segment identified user ID; 1-year persistent
      'ajs_group_id', // Segment group ID; 1-year persistent
    ],
    domains: ['calendly.com', 'cdn.segment.io', 'cdn.segment.com', 'api.segment.io'],
    docsUrl: 'https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/',
  },

  {
    key: 'google_analytics',
    displayName: 'Google Analytics (GA4 / gtag)',
    description: 'Google Analytics 4 loaded via gtag.js. Tracks page views and booking funnel'
      + ' events. Gated on the "performance" consent group. Universal Analytics (UA) is also'
      + ' present as a legacy integration.',
    category: 'performance',
    oneTrustCategoryId: 'C0002',
    primaryCookieNames: [
      '_ga', // GA client ID; 2-year persistent; set on calendly.com
      '_ga_*', // GA4 session/measurement ID cookie; 2-year persistent
      '_gat', // GA request throttle; 10-minute session
      '_gat_UA-*', // UA-specific throttle variant
      '_gcl_*', // Google Ads conversion/click tracking (_gcl_au, _gcl_aw, _gcl_dc, etc.)
      '_gac_UA-*', // Google Ads click attribution; keyed by UA property ID
    ],
    domains: [
      'calendly.com',
      'www.googletagmanager.com',
      'www.google-analytics.com',
      'region1.google-analytics.com',
    ],
    docsUrl: 'https://support.google.com/analytics/answer/11397207',
  },

  {
    key: 'meta_pixel',
    displayName: 'Meta (Facebook) Pixel',
    description: 'Facebook Pixel (fbevents.js) loaded for conversion tracking. Gated on the'
      + ' "targeting" consent group. Fires PageView and custom booking events via window.fbq.',
    category: 'targeting',
    oneTrustCategoryId: 'C0004',
    primaryCookieNames: [
      '_fbp', // Facebook browser ID; 90-day persistent; set by fbevents.js on calendly.com
      '_fbc', // Facebook click ID from URL fbclid param; 90-day persistent
    ],
    domains: ['calendly.com', 'connect.facebook.net', 'www.facebook.com'],
    docsUrl: 'https://developers.facebook.com/docs/meta-pixel/',
  },

  {
    key: 'datadog_rum',
    displayName: 'Datadog RUM & Session Replay',
    description: 'Datadog Real User Monitoring and optional Session Replay, initialized via'
      + ' @calendly/logging-service with configurable sample rates. Used for frontend error'
      + ' tracking and performance monitoring. Gated on the "performance" consent group.',
    category: 'performance',
    oneTrustCategoryId: 'C0002',
    primaryCookieNames: [
      '_dd_s', // RUM session cookie; up to 4-hour idle / 1-year max lifetime (SDK v6)
      'dd_cookie_test_*', // Probe cookie (dd_cookie_test_{uuid}); immediately removed after writability check
    ],
    domains: ['calendly.com', 'browser-intake-datadoghq.com'],
    docsUrl: 'https://docs.datadoghq.com/real_user_monitoring/browser/',
  },

  {
    key: 'stripe',
    displayName: 'Stripe Payments',
    description: 'Stripe.js loaded via @stripe/stripe-js for PCI-compliant payment collection'
      + ' on booking pages that require payment. Sets strictly necessary cookies for fraud detection.',
    category: 'necessary',
    oneTrustCategoryId: 'C0001',
    primaryCookieNames: [
      '__stripe_orig_props', // Stripe device fingerprint / fraud detection
      '__stripe_sid', // Stripe session identifier
      'm', // Stripe session state (m.stripe.com)
      '_mf', // Stripe fraud prevention (m.stripe.network)
    ],
    domains: ['calendly.com', 'js.stripe.com', 'm.stripe.com', 'm.stripe.network'],
    docsUrl: 'https://stripe.com/docs/js',
  },

  {
    key: 'paypal',
    displayName: 'PayPal SDK',
    description: 'PayPal JS SDK loaded for booking pages that accept PayPal payments.'
      + ' Sets a session cookie (SEGM) required for the checkout flow.',
    category: 'necessary',
    oneTrustCategoryId: 'C0001',
    primaryCookieNames: [
      'SEGM', // PayPal session management; session cookie
      // _gcl_au is a Google Ads conversion cookie, not a PayPal session cookie.
      // It is governed by the google_analytics vendor (performance / C0002) via the _gcl_* pattern.
    ],
    domains: ['calendly.com', 'www.paypal.com', 'www.sandbox.paypal.com'],
    docsUrl: 'https://developer.paypal.com/sdk/js/',
  },

  {
    key: 'recaptcha',
    displayName: 'Google reCAPTCHA',
    description: 'Google reCAPTCHA v3 (via @calendly/recaptcha) used to protect booking form'
      + ' submission from bots. Sets a functional cookie for the risk-score challenge.',
    category: 'functional',
    oneTrustCategoryId: 'C0003',
    primaryCookieNames: [
      '_GRECAPTCHA', // reCAPTCHA session token; persistent ~6 months
    ],
    domains: ['calendly.com', 'www.google.com', 'www.recaptcha.net'],
    docsUrl: 'https://developers.google.com/recaptcha/docs/v3',
  },

  {
    key: 'intercom',
    displayName: 'Intercom Messenger',
    description: 'Intercom Messenger Sheet Library (js.intercomcdn.com) loaded on the booking'
      + ' confirmation page for organizations using the Intercom integration. Submits booking'
      + ' details (profile name, event name, start time, timezone) to the connected Intercom inbox.'
      + ' NOTE: this script is not currently consent-gated — a functional (C0003) gate should be added.',
    category: 'functional',
    oneTrustCategoryId: 'C0003',
    primaryCookieNames: [
      'intercom-id-*', // Intercom visitor identity; persistent
      'intercom-session-*', // Intercom session token; persistent
      'intercom-device-id-*', // Intercom device fingerprint; persistent
    ],
    domains: ['calendly.com', 'js.intercomcdn.com', 'api-iam.intercom.io'],
    docsUrl: 'https://developers.intercom.com/installing-intercom/web/installation/',
  },
]
